import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ImageUploader } from "@/components/ImageUploader";
import { Bot, LogOut, Plus, Pencil, Trash2, Package, Inbox, Settings as SettingsIcon, Loader2, BarChart3 } from "lucide-react";

const STATUS_COLORS = {
  published: "bg-green-100 text-green-800",
  draft: "bg-amber-100 text-amber-800",
  archived: "bg-slate-100 text-slate-700",
  discontinued: "bg-red-100 text-red-700",
  new: "bg-brand-navy/10 text-brand-navy",
  contacted: "bg-blue-100 text-blue-700",
  closed: "bg-slate-100 text-slate-700",
};

const EMPTY_PRODUCT = {
  slug: "", name: "", brand: "", category: "domestic", status: "draft",
  verified: false, quote_only: false, price: "", area_m2: "", max_slope: "",
  image_url: "", uploaded_images: [], positioning: "", highlights: "", specs: "", support_content: "",
  application_guidance: "", placeholder: false, sort_order: 0,
};

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function BarList({ items }) {
  if (!items || items.length === 0) return <p className="mt-3 text-sm text-muted-foreground">No data yet.</p>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="mt-4 space-y-2">
      {items.map((i) => (
        <div key={i.key} className="flex items-center gap-3">
          <span className="w-40 shrink-0 truncate text-sm text-brand-ink" title={i.key}>{i.key}</span>
          <div className="flex-1 h-5 rounded-full bg-brand-navy/10 overflow-hidden">
            <div className="h-full rounded-full bg-brand-navy" style={{ width: `${(i.count / max) * 100}%` }} />
          </div>
          <span className="w-10 text-right font-mono-spec text-sm text-brand-navy">{i.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedEnq, setSelectedEnq] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const openNew = () => setEditing({ ...EMPTY_PRODUCT, _new: true });
  const openEdit = (p) => setEditing({
    ...p,
    price: p.price ?? "",
    area_m2: p.area_m2 ?? "",
    max_slope: p.max_slope ?? "",
    brand: p.brand ?? "",
    highlights: (p.highlights || []).join(", "),
    specs: Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n"),
  });

  const saveProduct = async () => {
    const p = editing;
    if (!p.name || !p.slug) { toast.error("Name and slug are required."); return; }
    const specs = {};
    (p.specs || "").split("\n").forEach((line) => {
      const idx = line.indexOf(":");
      if (idx > 0) specs[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    });
    const payload = {
      id: p.id, slug: p.slug, name: p.name, brand: p.brand || null,
      category: p.category, status: p.status, verified: !!p.verified,
      quote_only: !!p.quote_only,
      price: p.price === "" ? null : Number(p.price),
      area_m2: p.area_m2 === "" ? null : Number(p.area_m2),
      max_slope: p.max_slope === "" ? null : Number(p.max_slope),
      image_url: p.image_url || null,
      gallery: p.gallery || (p.image_url ? [p.image_url] : []),
      uploaded_images: p.uploaded_images || [],
      positioning: p.positioning || "", highlights: (p.highlights || "").split(",").map((s) => s.trim()).filter(Boolean),
      specs, support_content: p.support_content || "", application_guidance: p.application_guidance || "",
      placeholder: !!p.placeholder, sort_order: Number(p.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (p._new) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${p.id}`, payload);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const updateEnquiry = async (id, patch) => {
    try {
      const res = await api.put(`/admin/enquiries/${id}`, patch);
      setEnquiries((prev) => prev.map((e) => (e.id === id ? res.data : e)));
      if (selectedEnq?.id === id) setSelectedEnq(res.data);
      toast.success("Enquiry updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Settings saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const statCards = useMemo(() => stats ? [
    { label: "Total products", value: stats.total_products },
    { label: "Published", value: stats.published_products },
    { label: "Enquiries", value: stats.total_enquiries },
    { label: "New enquiries", value: stats.new_enquiries },
  ] : [], [stats]);

  return (
    <div className="min-h-screen bg-brand-white">
      <header className="bg-brand-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><span className="grid place-items-center h-9 w-9 rounded-lg bg-white text-brand-navy"><Bot className="h-5 w-5" /></span><span className="font-display font-extrabold">Admin Console</span></div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70 hidden sm:block">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={doLogout} className="text-white hover:bg-white/10" data-testid="admin-logout"><LogOut className="h-4 w-4 mr-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-brand-navy/10 p-5">
              <div className="text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">{s.label}</div>
              <div className="mt-1 font-display font-extrabold text-3xl text-brand-navy">{s.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="analytics" className="mt-8">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="analytics" data-testid="tab-analytics"><BarChart3 className="h-4 w-4 mr-1" /> Analytics</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products"><Package className="h-4 w-4 mr-1" /> Products</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries"><Inbox className="h-4 w-4 mr-1" /> Enquiries</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="h-4 w-4 mr-1" /> Settings</TabsTrigger>
          </TabsList>

          {/* Analytics */}
          <TabsContent value="analytics" className="mt-6">
            {analytics && (
              <div className="space-y-6" data-testid="admin-analytics">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: "Page views (all time)", value: analytics.total_views },
                    { label: "Page views today", value: analytics.views_today },
                    { label: "Total enquiries", value: analytics.total_enquiries },
                    { label: "New enquiries", value: analytics.new_enquiries },
                  ].map((s) => (
                    <div key={s.label} className="rounded-2xl bg-white border border-brand-navy/10 p-5">
                      <div className="text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">{s.label}</div>
                      <div className="mt-1 font-display font-extrabold text-3xl text-brand-navy">{s.value}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="rounded-2xl bg-white border border-brand-navy/10 p-5">
                    <h3 className="font-display font-semibold text-brand-ink">Enquiries by type</h3>
                    <BarList items={analytics.enquiries_by_type} />
                  </div>
                  <div className="rounded-2xl bg-white border border-brand-navy/10 p-5">
                    <h3 className="font-display font-semibold text-brand-ink">Enquiries by source page</h3>
                    <BarList items={analytics.enquiries_by_source} />
                  </div>
                  <div className="rounded-2xl bg-white border border-brand-navy/10 p-5">
                    <h3 className="font-display font-semibold text-brand-ink">Top pages (views)</h3>
                    <BarList items={analytics.views_by_path} />
                  </div>
                  <div className="rounded-2xl bg-white border border-brand-navy/10 p-5">
                    <h3 className="font-display font-semibold text-brand-ink">Views (last 14 days)</h3>
                    <BarList items={analytics.views_by_day} />
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Products */}
          <TabsContent value="products" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display font-bold text-xl text-brand-ink">Products ({products.length})</h2>
              <Button onClick={openNew} className="bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold" data-testid="admin-new-product"><Plus className="h-4 w-4 mr-1" /> New product</Button>
            </div>
            <div className="rounded-2xl bg-white border border-brand-navy/10 overflow-x-auto" data-testid="admin-products-table">
              <table className="w-full text-sm">
                <thead className="bg-brand-white text-left text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">
                  <tr><th className="p-3">Name</th><th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3">Price</th><th className="p-3">Flags</th><th className="p-3 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-brand-navy/10">
                      <td className="p-3 font-medium text-brand-ink">{p.name}<div className="text-xs text-muted-foreground">{p.brand}</div></td>
                      <td className="p-3 capitalize">{p.category.replace("_", " ")}</td>
                      <td className="p-3"><Badge className={STATUS_COLORS[p.status] + " capitalize"}>{p.status}</Badge></td>
                      <td className="p-3">{p.quote_only ? <span className="text-muted-foreground">Enquiry</span> : p.price != null ? `€${Number(p.price).toLocaleString("en-IE")}` : "—"}</td>
                      <td className="p-3 space-x-1">{p.verified && <Badge className="bg-brand-navy/10 text-brand-navy">verified</Badge>}{p.placeholder && <Badge className="bg-amber-100 text-amber-800">pending</Badge>}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteProduct(p)} className="text-red-600" data-testid={`delete-${p.slug}`}><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Enquiries */}
          <TabsContent value="enquiries" className="mt-6">
            <h2 className="font-display font-bold text-xl text-brand-ink mb-4">Enquiries ({enquiries.length})</h2>
            <div className="rounded-2xl bg-white border border-brand-navy/10 overflow-x-auto" data-testid="admin-enquiries-table">
              <table className="w-full text-sm">
                <thead className="bg-brand-white text-left text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">
                  <tr><th className="p-3">Name</th><th className="p-3">Type</th><th className="p-3">Product</th><th className="p-3">Contact</th><th className="p-3">Status</th><th className="p-3 text-right">View</th></tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No enquiries yet.</td></tr>}
                  {enquiries.map((e) => (
                    <tr key={e.id} className="border-t border-brand-navy/10">
                      <td className="p-3 font-medium text-brand-ink">{e.name}<div className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString("en-IE")}</div></td>
                      <td className="p-3 capitalize">{e.type.replace("_", " ")}</td>
                      <td className="p-3">{e.product_name || "—"}</td>
                      <td className="p-3 text-xs">{e.email}<br />{e.phone}</td>
                      <td className="p-3"><Badge className={STATUS_COLORS[e.status] + " capitalize"}>{e.status}</Badge></td>
                      <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => setSelectedEnq(e)} data-testid={`view-enquiry-${e.id}`}>Open</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings" className="mt-6">
            <h2 className="font-display font-bold text-xl text-brand-ink mb-4">Contact details</h2>
            {settings && (
              <div className="rounded-2xl bg-white border border-brand-navy/10 p-6 max-w-2xl grid gap-4" data-testid="admin-settings">
                {[["company_name","Company name"],["phone","Phone"],["email","Email"],["address","Address"],["hours","Opening hours"],["map_url","Map URL"]].map(([k, label]) => (
                  <div key={k}><Label>{label}</Label><Input value={settings[k] || ""} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))} data-testid={`settings-${k}`} /></div>
                ))}
                <Button onClick={saveSettings} className="w-fit bg-brand-navy hover:bg-brand-navy/90 text-white" data-testid="settings-save">Save settings</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Product editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-editor">
          <DialogHeader><DialogTitle className="font-display text-2xl text-brand-navy">{editing?._new ? "New product" : "Edit product"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input value={editing.name} onChange={(e) => { set("name", e.target.value); if (editing._new) set("slug", slugify(e.target.value)); }} data-testid="pf-name" /></div>
                <div><Label>Slug *</Label><Input value={editing.slug} onChange={(e) => set("slug", e.target.value)} onBlur={(e) => set("slug", slugify(e.target.value))} data-testid="pf-slug" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Brand</Label><Input value={editing.brand} onChange={(e) => set("brand", e.target.value)} data-testid="pf-brand" /></div>
                <div><Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="pf-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domestic">Domestic</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="line_marking">Line marking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="pf-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                      <SelectItem value="discontinued">Discontinued</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Sort order</Label><Input type="number" value={editing.sort_order} onChange={(e) => set("sort_order", e.target.value)} data-testid="pf-sort" /></div>
              </div>
              {editing.category === "domestic" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div><Label>Price (€)</Label><Input type="number" value={editing.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" /></div>
                  <div><Label>Area (m²)</Label><Input type="number" value={editing.area_m2} onChange={(e) => set("area_m2", e.target.value)} data-testid="pf-area" /></div>
                  <div><Label>Max slope (%)</Label><Input type="number" value={editing.max_slope} onChange={(e) => set("max_slope", e.target.value)} data-testid="pf-slope" /></div>
                </div>
              )}
              {editing.category !== "domestic" && <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2">Commercial & line-marking products are enquiry-only — no price is shown on the site.</p>}
              <div><Label>Product images</Label><div className="mt-2"><ImageUploader value={editing.uploaded_images || []} onChange={(v) => set("uploaded_images", v)} /></div></div>
              <div><Label>Description</Label><div className="mt-2"><RichTextEditor key={`pos-${editing.id || "new"}`} value={editing.positioning} onChange={(v) => set("positioning", v)} testId="pf-positioning" /></div></div>
              <div><Label>Highlights (comma-separated)</Label><Input value={editing.highlights} onChange={(e) => set("highlights", e.target.value)} data-testid="pf-highlights" /></div>
              <div><Label>Specifications (one per line: key: value)</Label><Textarea rows={3} value={editing.specs} onChange={(e) => set("specs", e.target.value)} data-testid="pf-specs" /></div>
              <div><Label>Support content</Label><div className="mt-2"><RichTextEditor key={`sup-${editing.id || "new"}`} value={editing.support_content} onChange={(v) => set("support_content", v)} testId="pf-support" /></div></div>
              <div className="flex gap-8">
                <label className="flex items-center gap-2"><Switch checked={!!editing.verified} onCheckedChange={(v) => set("verified", v)} data-testid="pf-verified" /> Verified</label>
                <label className="flex items-center gap-2"><Switch checked={!!editing.placeholder} onCheckedChange={(v) => set("placeholder", v)} data-testid="pf-placeholder" /> Pending approval</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveProduct} disabled={saving} className="bg-brand-navy hover:bg-brand-navy/90 text-white" data-testid="pf-save">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : "Save product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enquiry detail */}
      <Dialog open={!!selectedEnq} onOpenChange={(o) => !o && setSelectedEnq(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enquiry-detail">
          <DialogHeader><DialogTitle className="font-display text-2xl text-brand-navy">Enquiry</DialogTitle></DialogHeader>
          {selectedEnq && (
            <div className="space-y-3 text-sm">
              {[["Name", selectedEnq.name],["Email", selectedEnq.email],["Phone", selectedEnq.phone],["County", selectedEnq.county],["Type", selectedEnq.type],["Product", selectedEnq.product_name],["Source", selectedEnq.source_page],["Organisation", selectedEnq.organisation],["Area", selectedEnq.area_estimate],["Pitches", selectedEnq.pitches],["Service", selectedEnq.service_type],["Timeframe", selectedEnq.timeframe]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="text-brand-ink font-medium text-right">{v}</span></div>
              ))}
              {selectedEnq.message && <div className="rounded-lg bg-brand-white p-3 text-brand-ink">{selectedEnq.message}</div>}
              <div><Label>Status</Label>
                <Select value={selectedEnq.status} onValueChange={(v) => updateEnquiry(selectedEnq.id, { status: v })}>
                  <SelectTrigger data-testid="enquiry-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Staff notes</Label><Textarea rows={3} defaultValue={selectedEnq.staff_notes} onBlur={(e) => updateEnquiry(selectedEnq.id, { staff_notes: e.target.value })} data-testid="enquiry-notes" /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
